/**
 * design_handoff_auth §1/§4 — a holding route, not a dead end: it polls,
 * and flips to "Email confirmed" + auto-continue once the link is opened
 * in another tab. "← Start over" is a log-out and is honestly labelled
 * Log out.
 * Created by piotr@revisual.io on 28/07/2025.
 */

import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import AuthLayout from "./AuthPages/AuthLayout";
import TestimonialPane from "./AuthPages/TestimonialPane";
import AuthStateCard from "./AuthPages/AuthStateCard";
import AuthAlert from "./AuthPages/AuthAlert";
import { Input, Button, Icon } from "../theme/untitled";
import { useUserStore } from "../../../hooks/redux/user";
import isEmail from "validator/lib/isEmail";
import { rehydrateFromStorage } from "../store";
import useVisibilityHook from "../../../hooks/useVisibilityHook";
import { formatError } from "../../../helpers/errors";
import styles from "./AuthPages/authForm.module.scss";

const POLL_INTERVAL_MS = 5000;
const RESEND_COOLDOWN_SECONDS = 60;

const formatCountdown = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const VerifyEmailPage = () => {
  const history = useHistory();
  const { user, checkSession, resendActivationToken, editUser, logout } = useUserStore();
  const [isEmailEditEnabled, setIsEmailEditEnabled] = React.useState(false);
  const [emailIncorrect, setEmailIncorrect] = React.useState(false);
  const [redirecting, setRedirecting] = React.useState(false);

  const emailInputRef = useRef();
  // Latches permanently once a poll fails — user.sessionExpired itself gets
  // reset back to false by logout(), which would otherwise re-arm the
  // interval and loop: fail -> alert -> logout -> flag resets -> re-poll -> fail...
  const sessionDeadRef = useRef(false);

  const isLoading = user.resendActivation || user.edit;
  const isVerified = Boolean(user.model?.is_account_verified);

  const [timeRemainingToResend, setTimeRemainingToResend] = React.useState(RESEND_COOLDOWN_SECONDS);

  useVisibilityHook(() => {
    if (!isVerified) {
      rehydrateFromStorage();
    }
  }, [isVerified, checkSession]);

  useEffect(() => {
    if (user.sessionExpired) {
      sessionDeadRef.current = true;
    }
  }, [user.sessionExpired]);

  // Confirm your email is a holding route, not a dead end: it polls, so a
  // link opened in another tab flips this tab over on its own. Stops for
  // good once the session is known invalid (see sessionDeadRef above).
  useEffect(() => {
    if (isVerified || sessionDeadRef.current) return undefined;
    const timer = setInterval(() => {
      if (sessionDeadRef.current) {
        clearInterval(timer);
        return;
      }
      checkSession();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isVerified]);

  useEffect(() => {
    if (!isVerified) return;
    setRedirecting(true);
    const timer = setTimeout(() => history.push("/onboarding"), 1200);
    return () => clearTimeout(timer);
  }, [isVerified]);

  useEffect(() => {
    setTimeout(() => {
      setTimeRemainingToResend((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
  }, [timeRemainingToResend]);

  const onChangeEmailAddress = useCallback(
    (e) => {
      e.preventDefault();
      setIsEmailEditEnabled(!isEmailEditEnabled);
      if (!isEmailEditEnabled) {
        setTimeout(() => emailInputRef.current && emailInputRef.current.focus(), 100);
      }
    },
    [isEmailEditEnabled]
  );

  const onResendConfirmationEmail = useCallback(
    (e) => {
      e.preventDefault();
      if (timeRemainingToResend > 0) return;
      resendActivationToken();
      setTimeRemainingToResend(RESEND_COOLDOWN_SECONDS);
    },
    [timeRemainingToResend]
  );

  const onEditEmailAddress = useCallback(
    async (e) => {
      e.preventDefault();
      const email = emailInputRef.current.value;
      if (!isEmail(email)) {
        setEmailIncorrect(true);
        return;
      }
      setEmailIncorrect(false);
      setIsEmailEditEnabled(false);
      await editUser(user.model.uuid, { email }, false);
      resendActivationToken();
    },
    [user.model]
  );

  const onLogout = useCallback(async () => {
    await logout();
    window.location = "/login";
  }, []);

  const resendErrorMessage = useMemo(() => {
    if (!user.resendActivationError) return null;
    return formatError(user.resendActivationError, "Could not resend the confirmation email. Please try again.").join(" ");
  }, [user.resendActivationError]);

  if (isVerified) {
    return (
      <AuthLayout rightPane={<TestimonialPane />}>
        <AuthStateCard
          tone="success"
          icon="CheckCircle"
          title="Email confirmed"
          actions={
            <Button hierarchy="primary" loading disabled>
              Starting setup…
            </Button>
          }
        >
          <p>Taking you to setup — three short steps and your first widget is live.</p>
        </AuthStateCard>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout rightPane={<TestimonialPane />}>
      <form action="/" noValidate>
        <h1 className={styles.title}>Confirm your email</h1>
        <p className={styles.sub}>
          We sent a confirmation link to the address below. Open it and this page
          continues on its own — you can leave this tab where it is.
        </p>

        <div className={styles.fields}>
          <div>
            <div className={styles.labelRow}>
              <label htmlFor="verify-email-input" className={styles.fieldLabel}>Confirmation sent to</label>
              {!isEmailEditEnabled && !isLoading && (
                <button type="button" className={styles.linkButton} onClick={onChangeEmailAddress}>
                  Use a different address
                </button>
              )}
            </div>
            <Input
              id="verify-email-input"
              ref={emailInputRef}
              defaultValue={user.model?.email}
              readOnly={!isEmailEditEnabled}
              disabled={isLoading}
              type="email"
              autoComplete="off"
              error={emailIncorrect ? "Please enter a valid email address." : undefined}
            />
          </div>

          {isEmailEditEnabled && (
            <div className={styles.actionsRow}>
              <Button hierarchy="primary" size="sm" onClick={onEditEmailAddress}>
                Change email
              </Button>
              <Button hierarchy="secondaryGray" size="sm" onClick={onChangeEmailAddress}>
                Cancel
              </Button>
            </div>
          )}

          {!isEmailEditEnabled && (
            <>
              <p className={styles.hint}>
                <Icon name="HelpCircle" size={16} />
                <span>Nothing after a minute or two? Check spam, or resend below.</span>
              </p>

              {resendErrorMessage && <AuthAlert variant="error">{resendErrorMessage}</AuthAlert>}

              <Button
                hierarchy="secondaryGray"
                fullWidth
                iconLeading={<Icon name="RefreshCw02" size={16} />}
                disabled={timeRemainingToResend > 0 || isLoading}
                onClick={onResendConfirmationEmail}
              >
                {timeRemainingToResend > 0
                  ? `Resend in ${formatCountdown(timeRemainingToResend)}`
                  : "Resend confirmation email"}
              </Button>
            </>
          )}
        </div>

        <p className={styles.footLink}>
          <button type="button" className={styles.linkButton} onClick={onLogout}>
            <Icon name="LogOut01" size={16} /> Log out
          </button>
        </p>
      </form>
    </AuthLayout>
  );
};

export default VerifyEmailPage;
